import { useEffect } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { useDispatch } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import ContactsEmptyTable from "app/pages/sales-intelligence/common-components/ContactsEmptyTable/ContactsEmptyTable";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useContactsFetchEffect from "app/pages/sales-intelligence/modules/company-analysis/hooks/useContactsFetchEffect";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { initialActionsPanelFilters } from "app/pages/sales-intelligence/sub-modules/contacts-search/constants/initialState";
import { ContactsFlowProvider } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import { useInvalidateContactSearchResultAsync } from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useContactSearchResult";
import { actionsPanelFiltersChangedAction } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/action-creators";
import { useIsContactsAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-tab/hooks/useIsContactsAvailable";

import ContactDetailsDropdownContainer from "../../../components/contacts/ContactDetailsDropdown/ContactDetailsDropdownContainer";
import ContactsTableContainer from "../../../components/contacts/ContactsTable/ContactsTableContainer";
import EmployeeLocationDropdownContainer from "../../../components/contacts/EmployeeLocationDropdown/EmployeeLocationDropdownContainer";
import EmployeeProfilesDropdownContainer from "../../../components/contacts/EmployeeProfilesDropdown/EmployeeProfilesDropdownContainer";
import SearchTextBox from "../../../components/contacts/SearchTextBox/SearchTextBox";
import Card from "../../../components/shared/Card/Card";
import SectionTitle from "../../../components/shared/SectionTitle/SectionTitle";
import TableActionsPanelContainer from "../../../containers/contacts/TableActionsPanel/TableActionsPanelContainer";
import { useCompanyAnalysisContactsFlowStrategy } from "../../../hooks/useCompanyAnalysisContactsFlowStrategy";
import ContactRevealStatusDropdownContainer from "../../contacts/ContactRevealStatusDropdownContainer/ContactRevealStatusDropdownContainer";

import { StyledFiltersContainer } from "./styles";

const ContactsSectionRoot = () => {
    const dispatch = useDispatch();

    const { params } = useCurrentStateAndParamsWithFixedDomain();

    const contactsFlowStrategy = useCompanyAnalysisContactsFlowStrategy();

    const { isContactsVisible, isLoadingContactStatus, isContactsDisabledByCountry } = useIsContactsAvailable();

    const fetchData = useContactsFetchEffect(params.key);

    const invalidateContactSearchResult = useInvalidateContactSearchResultAsync();

    const refetchAfterTermsApproval = async () => {
        await Promise.all([fetchData(), invalidateContactSearchResult()]);
    };

    // The reveal-status filters are set from this section only, but live in the contacts-search slice
    // that other pages share, so they must not outlive it.
    useEffect(
        () => () => {
            dispatch(actionsPanelFiltersChangedAction(initialActionsPanelFilters));
        },
        [dispatch],
    );

    return (
        <ErrorBoundary fallbackRender={() => null}>
            <ContactsFlowProvider value={contactsFlowStrategy}>
                <Card
                    dataAutomation="contacts-card"
                    renderContent={() => (
                        <div>
                            {isContactsVisible || isLoadingContactStatus ? (
                                <>
                                    <SectionTitle
                                        text={translate("si.account-overview.contacts.title")}
                                        tooltipContent={
                                            <I18n
                                                translatable={{ key: "si.account-overview.contacts.title.tooltip" }}
                                            />
                                        }
                                    />
                                    <StyledFiltersContainer>
                                        <EmployeeProfilesDropdownContainer />
                                        <EmployeeLocationDropdownContainer />
                                        <ContactDetailsDropdownContainer />
                                        <ContactRevealStatusDropdownContainer />
                                    </StyledFiltersContainer>
                                    <SearchTextBox />
                                    <TableActionsPanelContainer onApprove={refetchAfterTermsApproval} />
                                </>
                            ) : null}

                            {isContactsDisabledByCountry ? (
                                <ContactsEmptyTable />
                            ) : (
                                <ContactsTableContainer onRefetchData={fetchData} />
                            )}
                        </div>
                    )}
                />
            </ContactsFlowProvider>
        </ErrorBoundary>
    );
};

export default ContactsSectionRoot;
